import { motion } from 'framer-motion';
import { ArrowRight, Calendar, Clock, User, Hash, Search } from 'lucide-react';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { BlogSidebar } from '@/components/blog/BlogSidebar';
import { useBlogs } from '@/hooks/useBlogs';

export default function Blog() {
  const { blogs, featuredBlogs, isLoading, isError } = useBlogs();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  // Filter blogs based on search and category
  const filteredPosts = blogs.filter((post) => {
    const matchesSearch =
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || post.tags.includes(selectedCategory);
    return matchesSearch && matchesCategory;
  });

  if (isLoading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center bg-[#F8F9FB] dark:bg-zinc-950">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Loading blog posts...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (isError) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center bg-[#F8F9FB] dark:bg-zinc-950">
          <div className="text-center">
            <p className="text-destructive">Failed to load blog posts. Please try again later.</p>
          </div>
        </div>
      </Layout>
    );
  }

  // Get unique tags for categories
  const allTags = Array.from(new Set(blogs.flatMap(post => post.tags)));
  const categories = ['All', ...allTags];

  return (
    <Layout>
      <div className="min-h-screen bg-[#F8F9FB] dark:bg-zinc-950">
        <section className="relative py-20 px-4 bg-white dark:bg-zinc-900 border-b border-zinc-200 dark:border-zinc-800">
          <div className="max-w-7xl mx-auto text-center">
            <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="space-y-4">
              <Badge className="bg-primary/10 text-primary hover:bg-primary/20 border-0 px-4 py-1">Our Blog</Badge>
              <h1 className="text-5xl md:text-6xl font-black text-zinc-900 dark:text-white tracking-tight">
                Insights for the <span className="text-primary">Next Generation</span>
              </h1>
              <p className="text-xl text-zinc-500 dark:text-zinc-400 max-w-2xl mx-auto">
                Discover the latest in QR technology, marketing strategies, and digital transformation.
              </p>
              
              {/* Search Bar */}
              <div className="max-w-2xl mx-auto mt-8 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 py-6 text-lg"
                />
              </div>
            </motion.div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 py-12 lg:py-20">
          <div className="flex flex-col lg:flex-row gap-12">
            <BlogSidebar 
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              categories={categories}
              posts={blogs}
            />

            <main className="flex-1 space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {filteredPosts.map((post, index) => (
                  <motion.article key={post.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }} className="group">
                    <Link to={`/blog/${post.slug}`}>
                      <div className="bg-white dark:bg-zinc-900 rounded-[2rem] overflow-hidden border border-zinc-200 dark:border-zinc-800 shadow-sm hover:shadow-2xl transition-all duration-500">
                        {post.image_url && (
                          <div className="aspect-[16/9] overflow-hidden">
                            <img
                              src={post.image_url}
                              alt={post.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                          </div>
                        )}
                        <div className="p-8 space-y-4">
                          <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-widest text-zinc-400">
                            <div className="flex items-center gap-4">
                              <div className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                <span>{new Date(post.published_at || post.created_at).toLocaleDateString()}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                <span>{post.read_time} min read</span>
                              </div>
                            </div>
                            <div className="flex gap-1">
                              {post.tags.slice(0, 2).map((tag, idx) => (
                                <Badge key={idx} variant="secondary" className="text-xs">
                                  {tag}
                                </Badge>
                              ))}
                            </div>
                          </div>
                          <h2 className="text-2xl font-black group-hover:text-primary transition-colors">{post.title}</h2>
                          <p className="text-zinc-500 dark:text-zinc-400 line-clamp-3 leading-relaxed">{post.excerpt}</p>
                          <div className="pt-4 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-xs">
                                {post.author[0]}
                              </div>
                              <span className="text-sm font-bold">{post.author}</span>
                            </div>
                            <div className="w-10 h-10 rounded-full border border-zinc-100 flex items-center justify-center group-hover:bg-primary transition-all">
                              <ArrowRight className="w-4 h-4 text-zinc-400 group-hover:text-white" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </motion.article>
                ))}
              </div>
              
              {filteredPosts.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-muted-foreground">No blog posts found. Try adjusting your search or category filter.</p>
                </div>
              )}
            </main>
          </div>
        </div>
      </div>
    </Layout>
  );
}
